import { Order as PrismaOrder, OrderStatus } from '@prisma/client';

export type Order = Partial<PrismaOrder>;

/**
 * Estado da sincronia do pedido com o PDV, derivado de `syncedAt` e da cfg
 * ORDER_SYNC_TIMEOUT_MINUTES. Não é coluna: é calculado na leitura.
 */
export type OrderSyncStatus = 'SYNCED' | 'WAITING' | 'LATE';

/**
 * Códigos de recusa de `POST /public/orders` (F6). O frontend seleciona a mensagem pelo CÓDIGO,
 * nunca pela mensagem (que é traduzida no cliente).
 *
 * `PRODUCT_UNAVAILABLE` cobre, com a MESMA resposta, produto inexistente, de outra empresa,
 * inativo, oculto no cardápio e sem preço no ponto de venda: distinguir os casos permitiria
 * enumerar catálogo alheio.
 */
export type OrderErrorCode =
  | 'IDEMPOTENCY_KEY_REQUIRED'
  | 'IDEMPOTENCY_KEY_INVALID'
  | 'IDEMPOTENCY_KEY_REUSE'
  | 'PRODUCT_UNAVAILABLE'
  | 'INVALID_OPTION'
  | 'ORDER_NOT_PERSISTED';

/**
 * M2-D78 — o motivo de um `400 INVALID_OPTION`, em `details.reason`.
 *
 * 🔴 **Motivo novo entra aqui, nunca como `OrderErrorCode` novo.** O cardápio que já está aberto (e ele
 * não tem prazo: o quiosque o mantém em tela cheia) cai em erro genérico com "Tentar novamente" para
 * todo código que não conhece, e o retry reenvia o mesmo corpo com a mesma chave e recebe o mesmo `400`,
 * em loop. O `INVALID_OPTION` ele conhece: sem retry, com "Altere o item e envie de novo". O bundle novo
 * lê o `reason` só para escolher uma mensagem mais específica.
 *
 * - `OPTION_NOT_FOUND`: id de opção que o produto não tem (ou que a guarda de leitura fechou).
 * - `GROUP_MIN` / `GROUP_MAX`: a contagem de opções distintas do grupo fora do `min` efetivo ou do `max`.
 * - `CLIENT_OUTDATED` (M2-D88): pedido sem o sinal de capacidade escolhendo adicional com preço — o
 *   cardápio antigo não exibe esse preço, e nunca se cobra o que a tela não mostrou.
 */
export const INVALID_OPTION_REASONS = ['OPTION_NOT_FOUND', 'GROUP_MIN', 'GROUP_MAX', 'CLIENT_OUTDATED'] as const;

export type InvalidOptionReason = (typeof INVALID_OPTION_REASONS)[number];

/** `details` do `400 INVALID_OPTION` (M2-D78). `groupId` é `null` quando o motivo não é de grupo. */
export interface InvalidOptionDetails {
  productId: string;
  groupId: string | null;
  reason: InvalidOptionReason;
}

/**
 * M2-D88 — o **sinal de capacidade** do cardápio, num header do `POST /public/orders`, fora do DTO e do
 * fingerprint: mudar o header nunca muda a chave de idempotência do carrinho.
 *
 * O valor é uma lista separada por vírgula, para os próximos deploys acrescentarem capacidade sem header
 * novo. `option-prices` diz "este cardápio mostra o preço do adicional e soma na prévia": sem ele, o
 * servidor recusa pedido com adicional pago (`INVALID_OPTION`, `CLIENT_OUTDATED`), porque o cardápio
 * antigo mostraria um preço e o servidor cobraria outro.
 *
 * O CORS da API reflete os headers do preflight (não há `allowedHeaders` fixo), como já faz com o
 * `Idempotency-Key`.
 */
export const CLIENT_CAPABILITIES_HEADER = 'X-Client-Capabilities';
export const CLIENT_CAPABILITY_OPTION_PRICES = 'option-prices';

/**
 * Tetos do `CreateOrderDto` — fonte ÚNICA para o DTO e para o frontend. A prévia do carrinho tem
 * de recusar o que a API vai recusar: estouro que só aparece no envio vira 400 do ValidationPipe
 * sem `code`, que o hóspede vê como erro genérico com um "Tentar novamente" que falha para sempre.
 */
export const MAX_ORDER_ITEM_QUANTITY = 99;
export const MAX_ORDER_ITEMS = 50;
export const MAX_ORDER_OPTION_IDS = 20;
/**
 * Nome de quem pediu (F14/D194). O `maxlength` do campo no carrinho sai daqui, então o hóspede é
 * impedido de digitar o 61º caractere em vez de descobrir o limite num `400` sem `code` no envio.
 */
export const MAX_ORDER_CUSTOMER_NAME_LENGTH = 60;

/**
 * Janela máxima da faixa `from`/`to` na listagem de pedidos (F7).
 *
 * O painel é operacional, não analítico. Desde a D53 nenhuma aba corta por data por default — as
 * finais são histórico `desc` com scroll —, e o seletor de dia é refinamento opcional. O teto
 * continua porque uma faixa de anos digitada errada varre a coleção. Fonte única para o DTO e para
 * o seletor de data da tela.
 */
export const MAX_ORDER_QUERY_WINDOW_DAYS = 92;

/**
 * Quantos pedidos cada aba do painel carrega por vez (D50). O scroll no fim da lista pede +20
 * **daquela** aba. Fonte única: é o default do servidor e o passo do "carregar mais" na tela — se
 * os dois divergirem, a janela pedida no poll deixa de casar com a que está na tela.
 */
export const ORDER_BOARD_PAGE_SIZE = 20;

/**
 * Teto da janela de UMA aba. O poll de 15s revalida a janela inteira (D51), então ela é o custo
 * recorrente da tela, não um custo de uma vez: sem teto, quem rola muito passa a puxar a coleção a
 * cada quinze segundos.
 */
export const MAX_ORDER_BOARD_WINDOW = 200;

/**
 * Status que formam a **fila de trabalho** — os únicos com contador no painel (D49).
 *
 * "Quantos entreguei desde sempre" não decide ação nenhuma do operador, e contar os finais era
 * exatamente o que forçava o `groupBy` a varrer o histórico inteiro. Contar só a fila é
 * naturalmente limitado: fila saudável tende a zero.
 *
 * É a lista dos status **não finais**, e o spec ao lado de `order-status.ts` trava essa
 * equivalência contra `ORDER_STATUS_TRANSITIONS` — o mapa continua a fonte da máquina de estados.
 */
export const ORDER_QUEUE_STATUSES = [OrderStatus.PENDING, OrderStatus.PREPARING, OrderStatus.READY] as const;

export type OrderQueueStatus = (typeof ORDER_QUEUE_STATUSES)[number];

/**
 * Status que **entram no extrato do hóspede** (F8) — os que ele consumiu e vai pagar.
 *
 * 🔴 **Lista POSITIVA de propósito, e não `{ not: CANCELED }`.** Uma denylist faz todo status novo no
 * enum ser **cobrado do hóspede por default**: o dia em que existir `REFUNDED`, `VOIDED` ou
 * `TRANSFERRED`, o extrato passa a somar aquilo em silêncio, sem ninguém tocar no código do extrato.
 * Falhar fechado aqui significa que status novo **não é cobrado** até alguém decidir explicitamente que
 * deve ser — e essa decisão vira uma linha nesta constante, num lugar só.
 *
 * O spec ao lado de `order-status.ts` trava a relação com o enum: se um `OrderStatus` novo aparecer sem
 * entrar aqui nem na lista de exclusões conhecidas, o teste falha e a decisão é forçada.
 */
export const ORDER_BILLABLE_STATUSES = [OrderStatus.PENDING, OrderStatus.PREPARING, OrderStatus.READY, OrderStatus.DELIVERED] as const;

export type OrderBillableStatus = (typeof ORDER_BILLABLE_STATUSES)[number];

/**
 * M2-D84 — a opção escolhida com grupo e preço, para o extrato, o resumo e o painel.
 *
 * Entra AO LADO do `options: string[]` (nomes), nunca no lugar: o bundle aberto durante o deploy — e ele
 * não tem prazo — desenharia `[object Object]` se o campo que ele lê mudasse de forma.
 *
 * `price` é o preço UNITÁRIO do adicional congelado no pedido; `0` é grátis e é o que sai para pedido
 * anterior à M2-F4. `groupName` é `null` na opção sem grupo e no pedido anterior (M2-D79).
 */
export interface OrderItemOptionDetail {
  name: string;
  groupName: string | null;
  price: number;
}

export interface OrderSummaryItem {
  productId: string;
  productName: string;
  quantity: number;
  /** M2-D77 — preço da UNIDADE MONTADA: produto mais adicionais. `unitPrice × quantity = totalPrice`. */
  unitPrice: number;
  totalPrice: number;
  /** Nomes das opções, como sempre. O bundle aberto durante o deploy lê este campo. */
  options: string[];
  /** M2-D84 — as mesmas opções com grupo e preço, na mesma ordem de `options`. */
  optionDetails: OrderItemOptionDetail[];
}

/**
 * Resposta autoritativa do envio do pedido. É daqui que a tela de confirmação
 * tira o total — nunca da soma feita no carrinho (D6).
 */
export interface OrderSummary {
  id: string;
  number: string;
  status: OrderStatus;
  subtotal: number;
  serviceFee: number;
  discount: number;
  total: number;
  createdAt: string;
  items: OrderSummaryItem[];
}

/**
 * Item de LISTAGEM de pedidos para o painel de acompanhamento (F7). Projeção estreita DE PROPÓSITO
 * (I9), NÃO `Partial<PrismaOrder>`: `idempotencyKey`, `idempotencyFingerprint`, `sessionId` e as
 * coordenadas (`latitude`/`longitude`/`geoAccuracy`) são dados internos/de auditoria e NUNCA vazam
 * para o frontend. A coleção guarda resumo e não hidrata `products` — o detalhe é `OrderSummary`,
 * um de cada vez. O backend DEVE materializar isto com `select` explícito, nunca `findMany` cru (F6).
 * Trocar por `Partial<PrismaOrder>` reabre o vazamento em silêncio.
 */
export type OrderListItem = Pick<
  PrismaOrder,
  | 'id'
  | 'number'
  // F15/D205 — número curto do dia (#042), o que o card mostra em destaque; `null` em pedido anterior à F15.
  | 'dailyNumber'
  | 'status'
  | 'locationNumber'
  | 'pointOfSaleName'
  | 'customerName'
  | 'currency'
  | 'subtotal'
  | 'serviceFee'
  | 'discount'
  | 'total'
  | 'syncedAt'
  | 'createdAt'
  | 'active'
>;

/**
 * Item do painel de acompanhamento (F7): o `OrderListItem` congelado mais o prazo de sincronia.
 *
 * `syncDueAt` é campo **derivado** (`createdAt` + cfg `ORDER_SYNC_TIMEOUT_MINUTES`), não coluna —
 * por isso entra num tipo à parte em vez de afrouxar a projeção do `OrderListItem`, que existe
 * justamente para impedir que campo interno vaze por descuido (I9). É `null` quando o pedido já
 * sincronizou: não há prazo a cumprir. Ver `resolveOrderSyncStatus` (D41).
 */
export type OrderBoardItem = OrderListItem & {
  syncDueAt: Date | null;
  /**
   * Itens do pedido. Vão na **listagem**, não só no detalhe: número e valor não dizem ao operador o
   * que precisa ser preparado, e o card sem isso obriga a abrir cada pedido para tocar a fila. Não
   * custa consulta — no MongoDB os itens são documento embutido no próprio pedido.
   */
  items: OrderSummaryItem[];
};

/**
 * Fatia de UMA aba do painel: a janela carregada daquele status e se existe mais além dela.
 *
 * `hasMore` sai de uma sonda (`take: janela + 1`), não de um `count`: contar o total de uma aba
 * final é a varredura de histórico que a D49 eliminou, e para decidir se o "carregar mais" aparece
 * basta saber que existe **um** pedido além da janela.
 */
export interface OrderBoardSlice {
  items: OrderBoardItem[];
  hasMore: boolean;
}

/**
 * Resposta de `GET /orders` (D42 evoluída pela D50): as cinco abas **e** o contador da fila, num
 * request único.
 *
 * **Por que as cinco fatias juntas:** trocar de aba deixa de disparar requisição — o dado já veio —
 * e o polling continua sendo 1 requisição a cada 15s, independente da aba aberta e de quantas abas
 * o operador visita. Era o contrário antes: cada troca de aba era um fetch, e o contador vinha de um
 * `groupBy` sobre o histórico inteiro.
 *
 * **Paginação por janela, nunca por offset** (D50): cada fatia é o PREFIXO da ordenação por
 * `createdAt` daquele status, do tamanho que o cliente pedir. Pedido **migra de aba** quando outro
 * operador muda o status, então `skip/take` pularia ou duplicaria item no "carregar mais" — a lista
 * de origem encolhe no meio. Crescer o prefixo é imune a isso: o resultado é sempre um começo
 * consistente da mesma ordenação, e é também o que o poll revalida (D51). A ordenação **não** tem
 * desempate por `id`, e é decisão: o índice `[companyId, status, createdAt]` cobre `createdAt` puro, e
 * ordem total obrigaria o Mongo a ordenar a partição inteira em memória a cada 15s.
 *
 * `counts` cobre **só a fila** (D49): aba final não tem contador.
 *
 * 🔴 **`newestQueuedAt` existe porque contador não detecta troca.** O alerta de pedido novo comparava
 * só `counts.PENDING` com a leitura anterior: se um pedido é aceito e outro entra entre duas
 * leituras (5s com socket vivo, 15s sem), o número não muda e o pedido novo entra calado — no
 * tablet da cozinha. `createdAt` do mais recente de cada aba de fila só avança quando nasce pedido,
 * então a troca deixa de se anular. Vem do **mesmo `groupBy`** que já produzia `counts`: nenhuma
 * consulta a mais.
 */
export interface OrderBoardResponse {
  slices: Record<OrderStatus, OrderBoardSlice>;
  counts: Record<OrderQueueStatus, number>;
  /** `createdAt` do pedido mais recente de cada aba de fila. `null` quando a aba está vazia. */
  newestQueuedAt: Record<OrderQueueStatus, Date | null>;
  /**
   * A empresa tem ao menos uma `Integration` **ativa** — é o que decide se o indicador de sincronia do
   * card e o recorte "só não integrados" existem na tela.
   *
   * 🔴 **Sem credencial ativa ninguém pode marcar `syncedAt`.** A autenticação da superfície
   * `/integration` casa `active: true`, e só ela escreve o campo. O prazo do `syncDueAt` então vence em
   * todo pedido, e o painel acendia o alerta vermelho de "não integrado com o PDV/PMS" em **100% dos
   * cards** de uma empresa que não integra com PDV nenhum — o estado observado depois de inativar as
   * integrações de uma empresa.
   *
   * Vem no board e não no item porque a pergunta é da **empresa**: por pedido seria o mesmo booleano
   * repetido N vezes. E vem em **toda** resposta, não no login, porque ativar a integração precisa
   * reacender o indicador no poll seguinte, sem o operador sair e entrar de novo.
   */
  syncTracked: boolean;
}

/**
 * Avisos da mudança de status. Ausente no caminho normal; presente quando a conta da mesa **não**
 * pôde ser ajustada como o esperado — e nesses casos a tela precisa dizer ao operador que o
 * acerto é no PDV, senão o valor divergente aparece sem explicação.
 *
 * - `ACCOUNT_CLOSED`: conta já liquidada; não é reescrita (D40).
 * - `ACCOUNT_MISSING`: pedido sem `accountId`, ou conta não encontrada.
 * - `ACCOUNT_NOT_ADJUSTED`: a subtração não fazia sentido sobre o valor que está no banco — valor
 *   ilegível (não finito, negativo, fora dos centavos exatos) **ou** desconto maior que o saldo.
 *   Nada foi escrito: gravar zero apagaria o que o PDV lançou (D10), e conta **alta** é detectável
 *   pelo hóspede enquanto conta **zerada** não é.
 *
 * Em todos os casos o status **muda**; o que não acontece é o ajuste da conta.
 *
 * - `SYNCED_ORDER_CANCELED` (F9/D88-b) é de outra natureza: a conta foi ajustada normalmente, mas o
 *   pedido **já havia sido importado pelo PDV** (`syncedAt` presente), então o cancelamento precisa
 *   ser refeito lá — a plataforma não tem como desfazer lançamento em sistema de terceiro. O
 *   parceiro descobre isso sozinho pelo loop de reconciliação
 *   (`GET /integration/orders?status=CANCELED&updatedSince=`), mas quem cancelou tem de saber na
 *   hora, senão o hóspede paga um item cancelado.
 *
 * ⚠️ **O campo é UM só, e a precedência é deliberada:** aviso de conta vence
 * `SYNCED_ORDER_CANCELED`. Conta errada é dinheiro errado agora, no extrato do hóspede; a
 * reconciliação com o PDV é acerto em outro sistema. E a informação não se perde: o `syncedAt` do
 * pedido vem na mesma resposta, em `order.syncedAt`.
 */
export const ORDER_STATUS_WARNINGS = ['ACCOUNT_CLOSED', 'ACCOUNT_MISSING', 'ACCOUNT_NOT_ADJUSTED', 'SYNCED_ORDER_CANCELED'] as const;

export type OrderStatusWarning = (typeof ORDER_STATUS_WARNINGS)[number];

/** Resposta de `PATCH /orders/:id/status` e de `POST /orders/:id/cancel`. */
export interface OrderStatusChangeResult {
  order: OrderBoardItem;
  warning?: OrderStatusWarning;
}

/**
 * Códigos de recusa das rotas **autenticadas** de pedido (F7), separados do `OrderErrorCode` do
 * cardápio porque são outra superfície e outro público.
 *
 * `INVALID_STATUS_TRANSITION` é `409`, não `400` (D37): o payload é válido, o **estado atual** é
 * que não permite — e é o código que o contrato do integrador já promete, para a F9 responder
 * igual pelo `POST /integration/orders/:id/delivered`.
 *
 * `ORDER_NOT_FOUND` (F9) cobre inexistente **e** pedido de outra empresa, com resposta idêntica: o
 * contrário permitiria sondar a existência de pedido alheio. O código entrou porque o contrato do
 * integrador o promete e a transição de status é o **mesmo** serviço para as duas superfícies —
 * traduzir o `404` no controller da integração seria manter dois nomes para a mesma recusa.
 */
export type OrderStatusErrorCode = 'INVALID_STATUS_TRANSITION' | 'ORDER_STATUS_NOT_PERSISTED' | 'ORDER_NOT_FOUND';
